import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./Portfolio.css";
import "./CivilCards.css";
import "./CivilWorkspacePolish.css";
import "./CivilMatterDetail.css";
import "./CivilNotes.css";
import ClientCaseDocuments from "./ClientCaseDocuments";
import { formatDateUS } from "../utils/dateFormat";
import {
  CIVIL_PROCESS_STATUS_LABELS,
  calculateCivilMatterOperationalStatus,
  isTerminalMatter,
  normalizeStatus,
} from "../constants/civilMatterStatus";

const API = window.__CRM_API__ || `${window.location.origin}/api`;
const FILTER_STORAGE_KEY = "qlf-civil-case-filters";
const CASES_PER_PAGE = 12;
const emptyTask = {
  titulo: "",
  descripcion: "",
  prioridad: "media",
  fecha_vencimiento: "",
  asignado_a: "",
};
const initialDraft = {
  cliente_id: "",
  tipo_proceso: "Habeas Corpus / Bond Hearing",
  estado_proceso: "evaluacion",
  numero_caso: "",
  tribunal: "",
};
const processTypes = [
  "Habeas Corpus / Bond Hearing",
  "Mandamus - Corte Federal",
  "Administrative Procedure Act (APA) - Corte Federal",
  "APA / Mandamus Federal",
  "Otro caso civil federal",
];
const fullName = (user) =>
  [user?.nombre, user?.apellido].filter(Boolean).join(" ").trim() || "Usuario";
const clientName = (client) =>
  client
    ? `${client.nombre || ""} ${client.apellido || ""}`.trim()
    : "Cliente pendiente";
const judgeName = (matter) => matter?.juez || matter?.magistrado || "";
const formatDate = (value) => formatDateUS(value, "Sin fecha programada");
const formatNoteMeta = (entry) => {
  const raw = String(entry?.fecha || entry?.fecha_creacion || "").trim();
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})(?:[T\s]+(\d{1,2}:\d{2})(?::\d{2})?)?/);
  if (match) {
    const date = formatDateUS(match[1]);
    return { date, time: match[2] || "Hora no disponible" };
  }
  return { date: raw || "Fecha no disponible", time: "Hora no disponible" };
};
const civilCourtDisplay = (matter) => {
  const legacy = String(matter?.tribunal || "").trim();
  if (/central district court california/i.test(legacy)) {
    return {
      court: "United States District Court",
      district: matter?.distrito || "Central District of California",
      division: matter?.division || "Pendiente",
    };
  }
  return {
    court: legacy || "Pendiente",
    district: matter?.distrito || "Pendiente",
    division: matter?.division || "Pendiente",
  };
};
const Field = ({ label, children }) => (
  <label className="civil-field">
    <span>{label}</span>
    {children}
  </label>
);

function Modal({ title, close, children, narrow = false }) {
  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className={`civil-modal ${narrow ? "civil-modal--narrow" : ""}`}>
        <header>
          <div>
            <p>CASOS CIVILES</p>
            <h2>{title}</h2>
          </div>
          <button type="button" onClick={close} aria-label="Cerrar">
            ×
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

function OperationalBadge({ matter }) {
  const status = calculateCivilMatterOperationalStatus(matter);
  return (
    <span
      className={`civil-health ${status.tone}`}
      title={status.reasons.map((reason) => reason.label).join(" · ")}
    >
      {status.label}
    </span>
  );
}

function ReadValue({ label, value, action }) {
  return (
    <div className="civil-read-value">
      <small>{label}</small>
      <b>{value || "Pendiente de registrar"}</b>
      {action}
    </div>
  );
}

const CivilCases = () => {
  const persisted = (() => {
    try {
      return JSON.parse(sessionStorage.getItem(FILTER_STORAGE_KEY) || "{}");
    } catch {
      return {};
    }
  })();
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [item, setItem] = useState(null);
  const [formData, setFormData] = useState(null);
  const [tab, setTab] = useState("Resumen");
  const [view, setView] = useState(persisted.view || "lista");
  const [query, setQuery] = useState(persisted.query || "");
  const [stateFilter, setStateFilter] = useState(
    persisted.stateFilter || "activos",
  );
  const [processFilter, setProcessFilter] = useState(
    persisted.processFilter || "todos",
  );
  const [courtFilter, setCourtFilter] = useState(
    persisted.courtFilter || "todos",
  );
  const [judgeFilter, setJudgeFilter] = useState(
    persisted.judgeFilter || "todos",
  );
  const [responsibleFilter, setResponsibleFilter] = useState(
    persisted.responsibleFilter || "todos",
  );
  const [attentionOnly, setAttentionOnly] = useState(
    Boolean(persisted.attentionOnly),
  );
  const [incompleteOnly, setIncompleteOnly] = useState(
    Boolean(persisted.incompleteOnly),
  );
  const [moreFilters, setMoreFilters] = useState(
    Boolean(persisted.moreFilters),
  );
  const [openTasksOnly, setOpenTasksOnly] = useState(
    Boolean(persisted.openTasksOnly),
  );
  const [withoutCaseNumber, setWithoutCaseNumber] = useState(
    Boolean(persisted.withoutCaseNumber),
  );
  const [dateRange, setDateRange] = useState(persisted.dateRange || "todos");
  const [newOpen, setNewOpen] = useState(false);
  const [draft, setDraft] = useState(initialDraft);
  const [taskOpen, setTaskOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [followUpNote, setFollowUpNote] = useState("");
  const [numberOpen, setNumberOpen] = useState(false);
  const [numberDraft, setNumberDraft] = useState("");
  const [task, setTask] = useState(emptyTask);
  const [isEditing, setIsEditing] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);

  const load = async () => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    const auth = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    const current = JSON.parse(localStorage.getItem("usuario") || "null");
    const [caseResponse, clientResponse, userResponse] = await Promise.all([
      axios.get(`${API}/civil-cases/`, auth),
      axios.get(`${API}/clientes/`, auth),
      axios.get(`${API}/auth/usuarios`, auth).catch(() => ({ data: [] })),
    ]);
    setCases(caseResponse.data || []);
    setClients(clientResponse.data || []);
    setUsers(
      userResponse.data?.length ? userResponse.data : current ? [current] : [],
    );
  };

  const open = async (id) => {
    try {
      setFeedback("");
      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token");
      const auth = token
        ? { headers: { Authorization: `Bearer ${token}` } }
        : {};
      const response = await axios.get(`${API}/civil-cases/${id}`, auth);
      setItem(response.data);
      setFormData(response.data);
      setTab("Resumen");
      setIsEditing(false);
      setShowMoreActions(false);
    } catch (error) {
      setFeedback(
        error.response?.data?.detail || "No fue posible abrir el expediente.",
      );
    }
  };

  useEffect(() => {
    load().catch(() => setFeedback("No fue posible cargar los expedientes."));
  }, []);
  useEffect(() => {
    sessionStorage.setItem(
      FILTER_STORAGE_KEY,
      JSON.stringify({
        view,
        query,
        stateFilter,
        processFilter,
        courtFilter,
        judgeFilter,
        responsibleFilter,
        attentionOnly,
        incompleteOnly,
        moreFilters,
        openTasksOnly,
        withoutCaseNumber,
        dateRange,
      }),
    );
  }, [
    view,
    query,
    stateFilter,
    processFilter,
    courtFilter,
    judgeFilter,
    responsibleFilter,
    attentionOnly,
    incompleteOnly,
    moreFilters,
    openTasksOnly,
    withoutCaseNumber,
    dateRange,
  ]);
  useEffect(() => {
    setPage(1);
  }, [
    view,
    query,
    stateFilter,
    processFilter,
    courtFilter,
    judgeFilter,
    responsibleFilter,
    attentionOnly,
    incompleteOnly,
    openTasksOnly,
    withoutCaseNumber,
    dateRange,
  ]);

  const currentUser = JSON.parse(localStorage.getItem("usuario") || "null");
  const canDelete = [
    "admin",
    "administrador",
    "managing attorney",
    "managing_attorney",
  ].includes(
    String(currentUser?.rol || "")
      .trim()
      .toLowerCase(),
  );
  const caseClient = (matter) =>
    clients.find((entry) => entry.id === matter.cliente_id) || matter.cliente;
  const openTasks = (matter) =>
    matter.open_tasks_count ??
    matter.tareas?.filter(
      (entry) => normalizeStatus(entry.estado) !== "completada",
    ).length ??
    0;
  const caseStatus = (matter) => calculateCivilMatterOperationalStatus(matter);

  const filteredCases = useMemo(
    () =>
      cases.filter((matter) => {
        const client =
          clients.find((entry) => entry.id === matter.cliente_id) ||
          matter.cliente;
        const search =
          `${clientName(client)} ${matter.numero_caso || ""} ${matter.tipo_proceso || ""} ${matter.tribunal || ""} ${judgeName(matter)} ${matter.abogado_responsable || ""}`.toLowerCase();
        const procedural = normalizeStatus(matter.estado_proceso);
        const operational = caseStatus(matter);
        const next = matter.proxima_fecha
          ? new Date(`${matter.proxima_fecha}T12:00:00`)
          : null;
        const now = new Date();
        const withinSeven =
          next &&
          next >= new Date(now.toDateString()) &&
          next <= new Date(now.getTime() + 7 * 86400000);
        return (
          search.includes(query.toLowerCase()) &&
          (stateFilter === "todos" ||
            (stateFilter === "activos"
              ? !isTerminalMatter(matter)
              : procedural === stateFilter)) &&
          (processFilter === "todos" ||
            matter.tipo_proceso === processFilter) &&
          (courtFilter === "todos" ||
            (matter.tribunal || "Sin asignar") === courtFilter) &&
          (judgeFilter === "todos" ||
            (judgeName(matter) || "Sin asignar") === judgeFilter) &&
          (responsibleFilter === "todos" ||
            (matter.abogado_responsable || "Sin asignar") ===
              responsibleFilter) &&
          (!attentionOnly ||
            (!isTerminalMatter(matter) && operational.requiresAttention)) &&
          (!incompleteOnly || operational.key === "informacion_incompleta") &&
          (!openTasksOnly || openTasks(matter) > 0) &&
          (!withoutCaseNumber || !matter.numero_caso) &&
          (dateRange === "todos" ||
            (dateRange === "7dias"
              ? withinSeven
              : next && next < new Date(now.toDateString())))
        );
      }),
    [
      cases,
      clients,
      query,
      stateFilter,
      processFilter,
      courtFilter,
      judgeFilter,
      responsibleFilter,
      attentionOnly,
      incompleteOnly,
      openTasksOnly,
      withoutCaseNumber,
      dateRange,
    ],
  );

  const priorities = useMemo(
    () =>
      cases
        .filter((matter) => !isTerminalMatter(matter))
        .map((matter) => ({ matter, status: caseStatus(matter) }))
        .filter(({ status }) => status.requiresAttention)
        .sort((a, b) => a.status.priority - b.status.priority),
    [cases],
  );
  const incompleteCases = useMemo(
    () =>
      cases.filter(
        (matter) =>
          !isTerminalMatter(matter) &&
          caseStatus(matter).key === "informacion_incompleta",
      ),
    [cases],
  );
  const resetFilters = () => {
    setQuery("");
    setStateFilter("activos");
    setProcessFilter("todos");
    setCourtFilter("todos");
    setJudgeFilter("todos");
    setResponsibleFilter("todos");
    setAttentionOnly(false);
    setIncompleteOnly(false);
    setOpenTasksOnly(false);
    setWithoutCaseNumber(false);
    setDateRange("todos");
  };

  const create = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await axios.post(`${API}/civil-cases/`, {
        ...draft,
        cliente_id: Number(draft.cliente_id),
      });
      setDraft(initialDraft);
      setNewOpen(false);
      await load();
      await open(response.data.id);
    } catch (error) {
      setFeedback(
        error.response?.data?.detail || "No se pudo crear el expediente.",
      );
    } finally {
      setSaving(false);
    }
  };
  const save = async () => {
    if (!formData) return;
    setSaving(true);
    try {
      await axios.put(`${API}/civil-cases/${item.id}`, formData);
      await load();
      await open(item.id);
      setFeedback("Expediente guardado correctamente.");
    } catch (error) {
      setFeedback(
        error.response?.data?.detail || "No se pudieron guardar los cambios.",
      );
    } finally {
      setSaving(false);
    }
  };
  const cancelEdit = () => {
    if (
      JSON.stringify(formData) !== JSON.stringify(item) &&
      !window.confirm("Hay cambios sin guardar. ¿Desea descartarlos?")
    )
      return;
    setFormData(item);
    setIsEditing(false);
  };
  const addTask = async (eventSubmit) => {
    eventSubmit.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/civil-cases/${item.id}/tasks`, {
        ...task,
        cliente_id: item.cliente_id,
      });
      setTask(emptyTask);
      setTaskOpen(false);
      await open(item.id);
    } catch (error) {
      setFeedback(
        error?.response?.data?.detail ||
          error?.userMessage ||
          "No se pudo crear la tarea.",
      );
    } finally {
      setSaving(false);
    }
  };
  const addFollowUpNote = async (eventSubmit) => {
    eventSubmit.preventDefault();
    const contenido = followUpNote.trim();
    if (!contenido) return;
    setSaving(true);
    try {
      await axios.post(`${API}/clientes/${item.cliente_id}/notas`, {
        contenido,
        civil_case_id: item.id,
      });
      setFollowUpNote("");
      setNoteOpen(false);
      await open(item.id);
      window.dispatchEvent(
        new CustomEvent("qlf-client-history-updated", {
          detail: { clienteId: item.cliente_id },
        }),
      );
      setFeedback("Nota de seguimiento guardada en el expediente y en el historial del cliente.");
    } catch (error) {
      setFeedback(
        error?.response?.data?.detail || "No se pudo guardar la nota de seguimiento.",
      );
    } finally {
      setSaving(false);
    }
  };
  const saveNumber = async (eventSubmit) => {
    eventSubmit.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/civil-cases/${item.id}/case-number`, {
        numero_caso: numberDraft,
      });
      setNumberOpen(false);
      await load();
      await open(item.id);
    } catch (error) {
      setFeedback(
        error.response?.data?.detail ||
          "No se pudo registrar el número de caso.",
      );
    } finally {
      setSaving(false);
    }
  };
  const archive = async () => {
    const reason = window.prompt("Motivo para archivar este expediente:");
    if (reason === null) return;
    await axios.put(`${API}/civil-cases/${item.id}`, {
      estado_proceso: "archivado",
      notas_internas: `${item.notas_internas || ""}\nArchivado: ${reason}`,
    });
    setItem(null);
    await load();
  };
  const remove = async () => {
    const reason = window.prompt(
      "Motivo obligatorio para eliminar este expediente:",
    );
    if (
      !reason ||
      !window.confirm("Esta acción eliminará el expediente. ¿Continuar?")
    )
      return;
    try {
      await axios.delete(
        `${API}/civil-cases/${item.id}?reason=${encodeURIComponent(reason)}`,
      );
      setItem(null);
      await load();
    } catch (error) {
      setFeedback(
        error.response?.data?.detail || "No se pudo eliminar el expediente.",
      );
    }
  };

  if (!item) {
    const processValues = [
      ...new Set(cases.map((entry) => entry.tipo_proceso).filter(Boolean)),
    ];
    const courtValues = [
      ...new Set(cases.map((entry) => entry.tribunal || "Sin asignar")),
    ];
    const judgeValues = [
      ...new Set(cases.map((entry) => judgeName(entry) || "Sin asignar")),
    ];
    const responsibleValues = [
      ...new Set(
        cases.map((entry) => entry.abogado_responsable || "Sin asignar"),
      ),
    ];
    const records = filteredCases
      .filter((entry) => view !== "plazos" || entry.proxima_fecha)
      .sort((a, b) =>
        String(a.proxima_fecha || "9999").localeCompare(
          String(b.proxima_fecha || "9999"),
        ),
      );
    const pageCount = Math.max(1, Math.ceil(records.length / CASES_PER_PAGE));
    const currentPage = Math.min(page, pageCount);
    const pageRecords = records.slice(
      (currentPage - 1) * CASES_PER_PAGE,
      currentPage * CASES_PER_PAGE,
    );
    return (
      <section className="portfolio civil-hub civil-redesign">
        <header className="civil-hub-header">
          <div>
            <p className="portfolio-eyebrow">LITIGIOS FEDERALES</p>
            <h1>Casos Civiles</h1>
            <p>
              Gestión centralizada de expedientes federales y litigios
              estratégicos.
            </p>
          </div>
          <div className="civil-header-actions">
            <span className="civil-total-chip">
              <b>{cases.length}</b> expedientes
            </span>
            <button className="civil-primary" onClick={() => setNewOpen(true)}>
              + Nuevo expediente
            </button>
          </div>
        </header>
        {feedback && <p className="civil-feedback">{feedback}</p>}
        <div className="civil-overview">
          <article
            onClick={() => {
              setStateFilter("activos");
              setAttentionOnly(false);
              setIncompleteOnly(false);
            }}
          >
            <span>📁</span>
            <div>
              <small>Expedientes activos</small>
              <strong>
                {cases.filter((entry) => !isTerminalMatter(entry)).length}
              </strong>
            </div>
          </article>
          <article
            onClick={() => {
              setStateFilter("todos");
              setAttentionOnly(true);
              setIncompleteOnly(false);
            }}
          >
            <span>🚨</span>
            <div>
              <small>Atención requerida</small>
              <strong>{priorities.length}</strong>
            </div>
          </article>
          <article
            onClick={() => {
              setStateFilter("activos");
              setAttentionOnly(false);
              setIncompleteOnly(true);
            }}
          >
            <span>🗂️</span>
            <div>
              <small>Datos incompletos</small>
              <strong>{incompleteCases.length}</strong>
            </div>
          </article>
          <article
            onClick={() => {
              setStateFilter("cerrado");
              setAttentionOnly(false);
              setIncompleteOnly(false);
            }}
          >
            <span>✓</span>
            <div>
              <small>Cerrados</small>
              <strong>
                {
                  cases.filter(
                    (entry) =>
                      normalizeStatus(entry.estado_proceso) === "cerrado",
                  ).length
                }
              </strong>
            </div>
          </article>
        </div>
        <section
          className={`civil-attention ${priorities.length ? "has-alerts" : "is-clear"}`}
        >
          <div>
            <p className="portfolio-eyebrow">ATENCIÓN REQUERIDA</p>
            <h2>
              {priorities.length
                ? "Prioridades del litigio"
                : "Sin alertas críticas"}
            </h2>
          </div>
          {priorities.length ? (
            <div className="civil-attention-list">
              <div className="civil-attention-list__headers">
                <span>Motivo</span>
                <span>Cliente / expediente</span>
                <span>Detalle</span>
                <span>Responsable</span>
                <span>Acción</span>
              </div>
              {priorities.slice(0, 6).map(({ matter, status }) => (
                <button key={matter.id} onClick={() => open(matter.id)}>
                  <OperationalBadge matter={matter} />
                  <b>
                    {clientName(caseClient(matter))}
                    <small>{matter.numero_caso || "Número pendiente"}</small>
                  </b>
                  <small>{status.reasons[0]?.label || status.label}</small>
                  <span>
                    {matter.abogado_responsable || "Responsable pendiente"}
                  </span>
                  <i>Abrir →</i>
                </button>
              ))}
            </div>
          ) : (
            <p className="civil-muted">
              No hay plazos vencidos ni acciones operativas pendientes.
            </p>
          )}
        </section>
        <section className="civil-ledger">
          <div className="civil-ledger-head">
            <div>
              <p className="portfolio-eyebrow">CARTERA DE EXPEDIENTES</p>
              <h2>Expedientes</h2>
            </div>
            <span>{records.length} resultados</span>
          </div>
          <div className="civil-filterbar">
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar cliente, número de caso, tribunal o juez"
            />
            <select
              value={stateFilter}
              onChange={(event) => setStateFilter(event.target.value)}
            >
              <option value="activos">Activos</option>
              <option value="todos">Todos</option>
              <option value="evaluacion">Evaluación</option>
              <option value="preparacion">Preparación</option>
              <option value="presentado">Presentado</option>
              <option value="en_litigio">En litigio</option>
              <option value="resuelto">Resuelto</option>
              <option value="cerrado">Cerrados</option>
              <option value="archivado">Archivados</option>
            </select>
            <select
              value={processFilter}
              onChange={(event) => setProcessFilter(event.target.value)}
            >
              <option value="todos">Tipo de proceso</option>
              {processValues.map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
            <select
              value={courtFilter}
              onChange={(event) => setCourtFilter(event.target.value)}
            >
              <option value="todos">Tribunal</option>
              {courtValues.map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
            <select
              value={judgeFilter}
              onChange={(event) => setJudgeFilter(event.target.value)}
            >
              <option value="todos">Juez</option>
              {judgeValues.map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
            <button
              type="button"
              className={moreFilters ? "active" : ""}
              onClick={() => setMoreFilters(!moreFilters)}
            >
              Más filtros
            </button>
            <button
              type="button"
              className="civil-clear"
              onClick={resetFilters}
            >
              Limpiar filtros
            </button>
            <nav>
              <button
                className={view === "lista" ? "active" : ""}
                onClick={() => setView("lista")}
              >
                Cuadrícula
              </button>
              <button
                className={view === "tabla" ? "active" : ""}
                onClick={() => setView("tabla")}
              >
                Tabla
              </button>
              <button
                className={view === "plazos" ? "active" : ""}
                onClick={() => setView("plazos")}
              >
                Próximos plazos
              </button>
            </nav>
          </div>
          {moreFilters && (
            <div className="civil-more-filters">
              <select
                value={responsibleFilter}
                onChange={(event) => setResponsibleFilter(event.target.value)}
              >
                <option value="todos">Responsable</option>
                {responsibleValues.map((value) => (
                  <option key={value}>{value}</option>
                ))}
              </select>
              <select
                value={dateRange}
                onChange={(event) => setDateRange(event.target.value)}
              >
                <option value="todos">Todas las fechas</option>
                <option value="7dias">En 7 días</option>
                <option value="vencidas">Vencidas</option>
              </select>
              <label>
                <input
                  type="checkbox"
                  checked={attentionOnly}
                  onChange={(event) => setAttentionOnly(event.target.checked)}
                />{" "}
                Atención requerida
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={incompleteOnly}
                  onChange={(event) => setIncompleteOnly(event.target.checked)}
                />{" "}
                Datos incompletos
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={openTasksOnly}
                  onChange={(event) => setOpenTasksOnly(event.target.checked)}
                />{" "}
                Con tareas abiertas
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={withoutCaseNumber}
                  onChange={(event) =>
                    setWithoutCaseNumber(event.target.checked)
                  }
                />{" "}
                Sin número de caso
              </label>
            </div>
          )}
          <div className={`civil-ledger-list view-${view}`}>
            {records.length ? (
              pageRecords.map((matter) => {
                const client = caseClient(matter);
                const court = civilCourtDisplay(matter);
                if (view === "lista") {
                  return (
                    <button
                      className="civil-case-card"
                      key={matter.id}
                      onClick={() => open(matter.id)}
                      aria-label={`Abrir expediente de ${clientName(client)}`}
                    >
                      <span className="civil-case-card__top">
                        <span className="civil-folder">⚖️</span>
                        <span
                          className={`civil-stage state-${normalizeStatus(matter.estado_proceso)}`}
                        >
                          {CIVIL_PROCESS_STATUS_LABELS[
                            normalizeStatus(matter.estado_proceso)
                          ] || matter.estado_proceso}
                        </span>
                      </span>
                      <span className="civil-case-card__identity">
                        <strong>{clientName(client)}</strong>
                        <small>{matter.numero_caso || "Número pendiente"}</small>
                      </span>
                      <span className="civil-case-card__process">
                        {matter.tipo_proceso || "Proceso pendiente"}
                      </span>
                      <span className="civil-case-card__facts">
                        <span>
                          <small>TRIBUNAL</small>
                          <b>{court.court}</b>
                        </span>
                        <span>
                          <small>PRÓXIMA FECHA</small>
                          <b>{formatDate(matter.proxima_fecha)}</b>
                        </span>
                        <span>
                          <small>RESPONSABLE</small>
                          <b>{matter.abogado_responsable || "Por asignar"}</b>
                        </span>
                        <span>
                          <small>TAREAS ABIERTAS</small>
                          <b>{openTasks(matter)}</b>
                        </span>
                      </span>
                      <span className="civil-case-card__footer">
                        <OperationalBadge matter={matter} />
                        <span className="civil-open">
                          Abrir expediente <b>→</b>
                        </span>
                      </span>
                    </button>
                  );
                }
                return (
                  <button
                    className="civil-list-row civil-ledger-row"
                    key={matter.id}
                    onClick={() => open(matter.id)}
                  >
                    <span className="civil-folder">⚖️</span>
                    <span className="civil-file-identity">
                      <strong>{clientName(client)}</strong>
                      <small>{matter.numero_caso || "Número pendiente"}</small>
                    </span>
                    <span className="civil-process" title={matter.tipo_proceso}>
                      <b>{matter.tipo_proceso || "Proceso pendiente"}</b>
                      <small>
                        {matter.proxima_fecha
                          ? `Próxima fecha: ${formatDate(matter.proxima_fecha)}`
                          : "Sin fecha registrada"}
                      </small>
                    </span>
                    <span className="civil-court">
                      <b>{matter.tribunal || "Tribunal pendiente"}</b>
                      <small>
                        {judgeName(matter) || "Juez pendiente"} ·{" "}
                        {matter.abogado_responsable || "Responsable pendiente"}
                      </small>
                    </span>
                    <span className="civil-task-count">
                      {openTasks(matter)} tareas abiertas
                    </span>
                    <span>
                      <OperationalBadge matter={matter} />
                    </span>
                    <span
                      className={`civil-stage state-${normalizeStatus(matter.estado_proceso)}`}
                    >
                      {CIVIL_PROCESS_STATUS_LABELS[
                        normalizeStatus(matter.estado_proceso)
                      ] || matter.estado_proceso}
                    </span>
                    <span className="civil-open">
                      Abrir expediente <b>→</b>
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="portfolio-empty">
                No hay expedientes para estos filtros. Ajuste la búsqueda o cree
                un nuevo expediente.
              </div>
            )}
          </div>
          {records.length > CASES_PER_PAGE && (
            <nav className="civil-pagination" aria-label="Paginación de expedientes">
              <span>
                Mostrando {(currentPage - 1) * CASES_PER_PAGE + 1}–
                {Math.min(currentPage * CASES_PER_PAGE, records.length)} de {records.length}
              </span>
              <div>
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setPage((value) => Math.max(1, value - 1))}
                  aria-label="Página anterior"
                >
                  ← Anterior
                </button>
                {Array.from({ length: pageCount }, (_, index) => index + 1).map(
                  (pageNumber) => (
                    <button
                      type="button"
                      key={pageNumber}
                      className={pageNumber === currentPage ? "active" : ""}
                      aria-current={pageNumber === currentPage ? "page" : undefined}
                      onClick={() => setPage(pageNumber)}
                    >
                      {pageNumber}
                    </button>
                  ),
                )}
                <button
                  type="button"
                  disabled={currentPage === pageCount}
                  onClick={() =>
                    setPage((value) => Math.min(pageCount, value + 1))
                  }
                  aria-label="Página siguiente"
                >
                  Siguiente →
                </button>
              </div>
            </nav>
          )}
        </section>
        {newOpen && (
          <Modal title="Nuevo expediente civil" close={() => setNewOpen(false)}>
            <form className="civil-form" onSubmit={create}>
              <Field label="Cliente">
                <select
                  required
                  value={draft.cliente_id}
                  onChange={(event) =>
                    setDraft({ ...draft, cliente_id: event.target.value })
                  }
                >
                  <option value="">Seleccionar cliente</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {clientName(client)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Tipo de proceso">
                <select
                  value={draft.tipo_proceso}
                  onChange={(event) =>
                    setDraft({ ...draft, tipo_proceso: event.target.value })
                  }
                >
                  {processTypes.map((type) => (
                    <option key={type}>{type}</option>
                  ))}
                </select>
              </Field>
              <Field label="Número de caso">
                <input
                  value={draft.numero_caso}
                  placeholder="Ej. 2:26-cv-00001"
                  onChange={(event) =>
                    setDraft({ ...draft, numero_caso: event.target.value })
                  }
                />
              </Field>
              <Field label="Tribunal">
                <input
                  value={draft.tribunal}
                  placeholder="United States District Court"
                  onChange={(event) =>
                    setDraft({ ...draft, tribunal: event.target.value })
                  }
                />
              </Field>
              <footer>
                <button type="button" onClick={() => setNewOpen(false)}>
                  Cancelar
                </button>
                <button className="civil-save" disabled={saving}>
                  {saving ? "Creando..." : "Crear expediente"}
                </button>
              </footer>
            </form>
          </Modal>
        )}
      </section>
    );
  }

  const current = formData || item;
  const operational = caseStatus(item);
  const followUpNotes =
    item.actividad?.filter((entry) =>
      String(entry.accion || "").startsWith("Nota de seguimiento:"),
    ) || [];
  const tabs = [
    { id: "Resumen", count: null },
    { id: "Tareas", count: item.tareas?.length || 0 },
    { id: "Documentos", count: item.documentos?.length || 0 },
    { id: "Actividad", count: item.actividad?.length || 0 },
    { id: "Notas", count: followUpNotes.length },
  ];
  const hasUnsavedChanges = JSON.stringify(formData) !== JSON.stringify(item);
  const update = (key, value) =>
    setFormData((before) => ({ ...before, [key]: value }));
  const navigateTab = (next) => setTab(next);
  const statusLabel =
    CIVIL_PROCESS_STATUS_LABELS[normalizeStatus(item.estado_proceso)] ||
    item.estado_proceso;
  const courtDisplay = civilCourtDisplay(item);

  return (
    <section
      className={`portfolio civil-file civil-redesign ${isEditing ? "is-editing" : ""}`}
    >
      <button
        className="civil-back"
        onClick={() => {
          if (
            isEditing &&
            hasUnsavedChanges &&
            !window.confirm("Hay cambios sin guardar. ¿Desea salir?")
          )
            return;
          setItem(null);
        }}
      >
        ← Volver a expedientes
      </button>
      <header className="civil-file-header">
        <div>
          <p className="portfolio-eyebrow">EXPEDIENTE CIVIL</p>
          <h1>{clientName(item.cliente)}</h1>
          <h2>{item.tipo_proceso || "Proceso pendiente"}</h2>
          <p className="civil-client-line">
            <span>{item.numero_caso || "Número de caso pendiente"}</span>
            <i>•</i>
            <span>{item.tribunal || "Tribunal pendiente"}</span>
          </p>
        </div>
        <div className="civil-header-actions">
          <span
            className={`civil-stage state-${normalizeStatus(item.estado_proceso)}`}
          >
            {statusLabel}
          </span>
          {!isEditing ? (
            <button
              type="button"
              className="civil-primary"
              onClick={() => {
                setFormData(item);
                setIsEditing(true);
              }}
            >
              Editar expediente
            </button>
          ) : (
            <>
              <button type="button" onClick={cancelEdit}>
                Cancelar
              </button>
              <button
                type="button"
                className="civil-primary"
                disabled={saving}
                onClick={save}
              >
                {saving ? "Guardando..." : "Guardar cambios"}
              </button>
            </>
          )}
          <div className="civil-more-actions">
            <button
              type="button"
              onClick={() => setShowMoreActions(!showMoreActions)}
            >
              Más acciones ▾
            </button>
            {showMoreActions && (
              <div className="civil-more-actions__menu">
                {isTerminalMatter(item) && (
                  <button
                    type="button"
                    onClick={async () => {
                      await axios.put(`${API}/civil-cases/${item.id}`, {
                        estado_proceso: "en_litigio",
                      });
                      await load();
                      await open(item.id);
                    }}
                  >
                    Reabrir
                  </button>
                )}
                <button type="button" onClick={archive}>
                  Archivar
                </button>
                {canDelete && (
                  <button
                    type="button"
                    className="civil-danger"
                    onClick={remove}
                  >
                    Eliminar
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </header>
      <div className="civil-case-strip">
        <button
          className="civil-strip-card"
          onClick={() => {
            setIsEditing(true);
            navigateTab("Resumen");
          }}
        >
          <small>JUEZ ASIGNADO</small>
          <b>{judgeName(item) || "Pendiente"}</b>
          <em>{judgeName(item) ? "Ver datos del proceso" : "Asignar juez"}</em>
        </button>
        <button
          className="civil-strip-card"
          onClick={() => {
            setIsEditing(true);
            navigateTab("Resumen");
          }}
        >
          <small>ABOGADO RESPONSABLE</small>
          <b>{item.abogado_responsable || "Pendiente"}</b>
          <em>
            {item.abogado_responsable
              ? "Ver responsable"
              : "Asignar responsable"}
          </em>
        </button>
        <button
          className="civil-strip-card"
          onClick={() => {
            setIsEditing(true);
            navigateTab("Resumen");
          }}
        >
          <small>PRÓXIMA FECHA</small>
          <b>
            {item.proxima_fecha
              ? formatDate(item.proxima_fecha)
              : "Sin fecha programada"}
          </b>
          <em>
            {item.proxima_fecha ? "Editar próxima fecha" : "Programar fecha"}
          </em>
        </button>
        <button
          className="civil-strip-card"
          onClick={() => navigateTab("Tareas")}
        >
          <small>TAREAS ABIERTAS</small>
          <b>{openTasks(item)}</b>
          <em>{openTasks(item) ? "Abrir tareas" : "Crear tarea"}</em>
        </button>
      </div>
      {isTerminalMatter(item) ? (
        <div className="civil-terminal-note">
          <b>Expediente cerrado</b> — no existen acciones operativas pendientes.
        </div>
      ) : operational.requiresAttention ? (
        <div className="civil-detail-attention">
          <b>{operational.label}</b>
          <span>
            {operational.reasons
              .map((reason) => reason.label)
              .slice(0, 3)
              .join(" · ")}
          </span>
        </div>
      ) : operational.missingFields.length ? (
        <div className="civil-detail-incomplete">
          <div>
            <b>
              Faltan {operational.missingFields.length} datos del expediente
            </b>
            <span>{operational.missingFields.join(" · ")}</span>
          </div>
          <button type="button" onClick={() => setIsEditing(true)}>
            Completar información
          </button>
        </div>
      ) : null}
      {feedback && <p className="civil-feedback">{feedback}</p>}
      <nav className="civil-tabs">
        {tabs.map(({ id, count }) => (
          <button
            type="button"
            className={tab === id ? "active" : ""}
            onClick={() => setTab(id)}
            key={id}
          >
            {id}
            {count !== null ? <span>{count}</span> : null}
          </button>
        ))}
      </nav>
      {isEditing && (
        <div
          className="civil-edit-footer"
          role="region"
          aria-label="Acciones de edición"
        >
          <span>
            {hasUnsavedChanges ? "Cambios sin guardar" : "Edición activa"}
          </span>
          <div>
            <button type="button" onClick={cancelEdit}>
              Cancelar
            </button>
            <button
              type="button"
              className="civil-primary"
              disabled={saving}
              onClick={save}
            >
              {saving ? "Guardando..." : "Guardar cambios"}
            </button>
          </div>
        </div>
      )}
      {tab === "Resumen" && (
        <div className="civil-summary">
          <div className="civil-card civil-process-card">
            <div className="civil-card-heading">
              <span>⚖️</span>
              <div>
                <p className="portfolio-eyebrow">INFORMACIÓN PRINCIPAL</p>
                <h3>Datos del proceso</h3>
              </div>
              {!item.numero_caso && !isEditing && (
                <button
                  className="civil-inline-action"
                  onClick={() => {
                    setNumberDraft("");
                    setNumberOpen(true);
                  }}
                >
                  + Registrar número de caso
                </button>
              )}
              {item.numero_caso && !isEditing && (
                <button
                  className="civil-inline-action"
                  onClick={() => {
                    setNumberDraft(item.numero_caso);
                    setNumberOpen(true);
                  }}
                >
                  Editar número de caso
                </button>
              )}
            </div>
            {isEditing ? (
              <div className="civil-edit-sections">
                <section>
                  <h4>A. Identificación del caso</h4>
                  <div className="civil-fields">
                    <Field label="Número de caso">
                      <input
                        value={current.numero_caso || ""}
                        onChange={(event) =>
                          update("numero_caso", event.target.value)
                        }
                      />
                    </Field>
                    <Field label="Tipo de proceso">
                      <select
                        value={current.tipo_proceso || ""}
                        onChange={(event) =>
                          update("tipo_proceso", event.target.value)
                        }
                      >
                        {processTypes.map((value) => (
                          <option key={value}>{value}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Estado procesal">
                      <select
                        value={current.estado_proceso || "evaluacion"}
                        onChange={(event) =>
                          update("estado_proceso", event.target.value)
                        }
                      >
                        {Object.entries(CIVIL_PROCESS_STATUS_LABELS).map(
                          ([value, label]) => (
                            <option value={value} key={value}>
                              {label}
                            </option>
                          ),
                        )}
                      </select>
                    </Field>
                    <Field label="Fecha de apertura">
                      <input
                        type="date"
                        value={current.fecha_apertura || ""}
                        onChange={(event) =>
                          update("fecha_apertura", event.target.value)
                        }
                      />
                    </Field>
                    <Field label="Fecha de cierre">
                      <input
                        type="date"
                        value={current.fecha_cierre || ""}
                        onChange={(event) =>
                          update("fecha_cierre", event.target.value)
                        }
                      />
                    </Field>
                    <Field label="Próxima fecha / plazo">
                      <input
                        type="date"
                        value={current.proxima_fecha || ""}
                        onChange={(event) =>
                          update("proxima_fecha", event.target.value)
                        }
                      />
                    </Field>
                  </div>
                </section>
                <section>
                  <h4>B. Tribunal</h4>
                  <div className="civil-fields">
                    <Field label="Tribunal federal">
                      <input
                        value={current.tribunal || ""}
                        onChange={(event) =>
                          update("tribunal", event.target.value)
                        }
                      />
                    </Field>
                    <Field label="Distrito">
                      <input
                        value={current.distrito || ""}
                        onChange={(event) =>
                          update("distrito", event.target.value)
                        }
                      />
                    </Field>
                    <Field label="División">
                      <input
                        value={current.division || ""}
                        onChange={(event) =>
                          update("division", event.target.value)
                        }
                      />
                    </Field>
                    <Field label="Juez">
                      <input
                        value={current.juez || ""}
                        onChange={(event) => update("juez", event.target.value)}
                      />
                    </Field>
                    <Field label="Magistrado">
                      <input
                        value={current.magistrado || ""}
                        onChange={(event) =>
                          update("magistrado", event.target.value)
                        }
                      />
                    </Field>
                  </div>
                </section>
                <section>
                  <h4>C. Representación y partes</h4>
                  <div className="civil-fields">
                    <Field label="Abogado responsable">
                      <select
                        value={current.abogado_responsable || ""}
                        onChange={(event) =>
                          update("abogado_responsable", event.target.value)
                        }
                      >
                        <option value="">Sin asignar</option>
                        {users.map((user) => (
                          <option key={user.id} value={fullName(user)}>
                            {fullName(user)} · {user.rol || "Usuario"}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Demandante / Petitioner">
                      <input
                        value={current.parte_demandante || ""}
                        onChange={(event) =>
                          update("parte_demandante", event.target.value)
                        }
                      />
                    </Field>
                    <Field label="Demandado / Respondent">
                      <input
                        value={current.parte_demandada || ""}
                        onChange={(event) =>
                          update("parte_demandada", event.target.value)
                        }
                      />
                    </Field>
                    <Field label="Agencia">
                      <input
                        value={current.agencia || ""}
                        onChange={(event) =>
                          update("agencia", event.target.value)
                        }
                      />
                    </Field>
                  </div>
                </section>
                <section>
                  <h4>D. Enlaces y notas</h4>
                  <Field label="Enlace externo">
                    <input
                      type="url"
                      value={current.enlace_externo || ""}
                      onChange={(event) =>
                        update("enlace_externo", event.target.value)
                      }
                    />
                  </Field>
                  <Field label="Notas internas">
                    <textarea
                      value={current.notas_internas || ""}
                      onChange={(event) =>
                        update("notas_internas", event.target.value)
                      }
                    />
                  </Field>
                </section>
              </div>
            ) : (
              <div className="civil-read-columns">
                <div>
                  <ReadValue label="Número de caso" value={item.numero_caso} />
                  <ReadValue
                    label="Tipo de proceso"
                    value={item.tipo_proceso}
                  />
                  <ReadValue
                    label="Tribunal federal"
                    value={courtDisplay.court}
                  />
                  <ReadValue
                    label="Distrito / división"
                    value={`${courtDisplay.district} / ${courtDisplay.division}`}
                  />
                  <ReadValue
                    label="Juez / magistrado"
                    value={[item.juez, item.magistrado]
                      .filter(Boolean)
                      .join(" / ")}
                  />
                </div>
                <div>
                  <ReadValue label="Estado procesal" value={statusLabel} />
                  <ReadValue
                    label="Abogado responsable"
                    value={item.abogado_responsable}
                  />
                  <ReadValue
                    label="Fecha de apertura"
                    value={formatDate(item.fecha_apertura)}
                  />
                  <ReadValue
                    label="Partes"
                    value={[item.parte_demandante, item.parte_demandada]
                      .filter(Boolean)
                      .join(" v. ")}
                  />
                  <ReadValue label="Agencia" value={item.agencia} />
                </div>
              </div>
            )}
          </div>
          <aside className="civil-card civil-next">
            <span className="civil-card-icon">→</span>
            <p className="portfolio-eyebrow">SEGUIMIENTO</p>
            <h3>Próximos pasos</h3>
            <div className="civil-next-list">
              <p>
                <b>Próxima fecha</b>
                <span>{formatDate(item.proxima_fecha)}</span>
              </p>
              <p>
                <b>Tarea prioritaria</b>
                <span>
                  {item.tareas?.find(
                    (entry) => normalizeStatus(entry.estado) !== "completada",
                  )?.titulo || "Sin tarea pendiente"}
                </span>
              </p>
              <p>
                <b>Última actividad</b>
                <span>
                  {item.actividad?.[0]?.accion || "Sin actividad registrada"}
                </span>
              </p>
              <p>
                <b>Último documento</b>
                <span>
                  {item.documentos?.[0]?.nombre || "Sin documentos vinculados"}
                </span>
              </p>
            </div>
            <button className="civil-primary" onClick={() => setTaskOpen(true)}>
              + Crear tarea
            </button>
            <button className="civil-audience-action" onClick={() => setNoteOpen(true)}>
              + Agregar nota
            </button>
          </aside>
        </div>
      )}
      {tab === "Tareas" && (
        <div className="civil-card">
          <div className="civil-section-head">
            <div>
              <h3>Tareas del expediente</h3>
              <p className="civil-muted">
                Seguimiento centralizado con el cliente y el equipo.
              </p>
            </div>
            <button className="civil-primary" onClick={() => setTaskOpen(true)}>
              + Crear tarea
            </button>
          </div>
          {item.tareas?.length ? (
            item.tareas.map((entry) => (
              <div className="civil-item" key={entry.id}>
                <span>
                  {normalizeStatus(entry.estado) === "completada" ? "✓" : "○"}
                </span>
                <div>
                  <b>{entry.titulo}</b>
                  <small>
                    {entry.asignado_a || "Sin asignar"} ·{" "}
                    {entry.fecha_vencimiento || "Sin vencimiento"} · Prioridad{" "}
                    {entry.prioridad}
                  </small>
                </div>
              </div>
            ))
          ) : (
            <p className="civil-muted">No hay tareas para este expediente.</p>
          )}
        </div>
      )}
      {tab === "Documentos" && <ClientCaseDocuments clienteId={item.cliente_id} civilCaseId={item.id} />}
      {tab === "Actividad" && (
        <div className="civil-card">
          <h3>Historial de actividad</h3>
          {item.actividad?.length ? (
            item.actividad.map((activity) => (
              <div className="civil-item" key={activity.id}>
                <span>•</span>
                <div>
                  <b>{activity.accion}</b>
                  <small>
                    {activity.fecha} · {activity.usuario}
                  </small>
                </div>
              </div>
            ))
          ) : (
            <p className="civil-muted">Aún no hay actividad registrada.</p>
          )}
        </div>
      )}
      {tab === "Notas" && (
        <div className="civil-card civil-follow-up-notes">
          <div className="civil-section-head">
            <div>
              <h3>Notas de seguimiento</h3>
              <p className="civil-muted">
                Cada nota queda registrada en este expediente y en el historial del cliente.
              </p>
            </div>
            <button className="civil-primary" onClick={() => setNoteOpen(true)}>
              + Agregar nota
            </button>
          </div>
          {followUpNotes.length ? (
            followUpNotes.map((entry) => {
                const meta = formatNoteMeta(entry);
                return (
                  <article className="civil-follow-up-note" key={entry.id}>
                    <p>{String(entry.accion).replace(/^Nota de seguimiento:\s*/, "")}</p>
                    <small>Fecha: {meta.date} · Hora: {meta.time} · Por: {entry.usuario || "Usuario"}</small>
                  </article>
                );
              })
          ) : (
            <div className="civil-notes-empty">
              <b>Aún no hay notas de seguimiento.</b>
              <span>Agregue la primera nota para que el equipo vea el contexto en el expediente y el perfil del cliente.</span>
            </div>
          )}
        </div>
      )}
      {taskOpen && (
        <Modal title="Crear tarea relacionada" close={() => setTaskOpen(false)}>
          <form className="civil-form" onSubmit={addTask}>
            <Field label="Título">
              <input
                required
                value={task.titulo}
                onChange={(event) =>
                  setTask({ ...task, titulo: event.target.value })
                }
              />
            </Field>
            <Field label="Vencimiento">
              <input
                type="date"
                value={task.fecha_vencimiento}
                onChange={(event) =>
                  setTask({ ...task, fecha_vencimiento: event.target.value })
                }
              />
            </Field>
            <Field label="Asignar a">
              <select
                value={task.asignado_a}
                onChange={(event) =>
                  setTask({ ...task, asignado_a: event.target.value })
                }
              >
                <option value="">Sin asignar</option>
                {users.map((user) => (
                  <option key={user.id} value={fullName(user)}>
                    {fullName(user)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Prioridad">
              <select
                value={task.prioridad}
                onChange={(event) =>
                  setTask({ ...task, prioridad: event.target.value })
                }
              >
                <option value="alta">Alta</option>
                <option value="media">Media</option>
                <option value="baja">Baja</option>
              </select>
            </Field>
            <Field label="Descripción">
              <textarea
                value={task.descripcion}
                onChange={(event) =>
                  setTask({ ...task, descripcion: event.target.value })
                }
              />
            </Field>
            <footer>
              <button type="button" onClick={() => setTaskOpen(false)}>
                Cancelar
              </button>
              <button className="civil-save" disabled={saving}>
                {saving ? "Guardando..." : "Crear tarea"}
              </button>
            </footer>
          </form>
        </Modal>
      )}
      {noteOpen && (
        <Modal title="Nueva nota de seguimiento" close={() => setNoteOpen(false)} narrow>
          <form className="civil-form civil-note-form" onSubmit={addFollowUpNote}>
            <div className="civil-note-callout">
              Esta nota se mostrará en el historial del cliente y en la actividad de este expediente.
            </div>
            <Field label="Nota">
              <textarea
                autoFocus
                required
                rows="7"
                value={followUpNote}
                onChange={(event) => setFollowUpNote(event.target.value)}
                placeholder="Registre el seguimiento, instrucciones o contexto relevante para el equipo."
              />
            </Field>
            <footer>
              <button type="button" onClick={() => setNoteOpen(false)}>
                Cancelar
              </button>
              <button className="civil-save" disabled={saving || !followUpNote.trim()}>
                {saving ? "Guardando..." : "Guardar nota"}
              </button>
            </footer>
          </form>
        </Modal>
      )}
      {numberOpen && (
        <Modal
          title={
            item.numero_caso
              ? "Editar número de caso"
              : "Registrar número de caso"
          }
          close={() => setNumberOpen(false)}
          narrow
        >
          <form className="civil-form" onSubmit={saveNumber}>
            <Field label="Número de caso">
              <input
                autoFocus
                required
                value={numberDraft}
                placeholder="Ej. 2:26-cv-00001"
                onChange={(event) => setNumberDraft(event.target.value)}
              />
            </Field>
            <footer>
              <button type="button" onClick={() => setNumberOpen(false)}>
                Cancelar
              </button>
              <button className="civil-save" disabled={saving}>
                {saving ? "Guardando..." : "Guardar número"}
              </button>
            </footer>
          </form>
        </Modal>
      )}
    </section>
  );
};

const CasePortfolio = ({ area }) =>
  area === "civil" ? <CivilCases /> : <CivilCases />;
export default CasePortfolio;
